import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useApiClient } from "@/hooks/useApiClient";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { GetRegistryChangeAlertSummaryAPI } from "@/features/registryChangeAlert/api";
import { SectionChip } from "@/features/registryChangeAlert/components/SectionChip";
import { formatReceivedDate } from "@/features/registryChangeAlert/utils/makeColumns";
import {
  REGISTRY_CHANGE_ALERT_PATH,
  REGISTRY_CHANGE_ALERT_TITLE,
} from "@/features/registryChangeAlert/utils/constants";

interface IProps {
  /** 機能フラグ。false の間は取得も表示もしない。 */
  enabled: boolean;
}

/**
 * 取得済み登記トップ・マイページに置く変動アラートのセクション。
 * アラートが 1 件も無ければ何も表示しない。
 */
export const RegistryChangeAlertSummarySection: React.FC<IProps> = ({
  enabled,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { apiClient } = useApiClient();
  const { data } = GetRegistryChangeAlertSummaryAPI(apiClient, enabled);

  if (!enabled || data === undefined || data.total === 0) {
    return null;
  }

  const accent = theme.palette.warning.main;

  return (
    <Box
      data-testid="registry-change-alert-summary"
      sx={{
        p: { xs: 2.5, [MOBILE_BREAKPOINT]: 3 },
        borderRadius: "8px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(accent, 0.5)}`,
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <NotificationsActiveOutlinedIcon sx={{ color: accent }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {REGISTRY_CHANGE_ALERT_TITLE}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            {data.total.toLocaleString()} 件
          </Typography>
          {data.unreadCount > 0 && (
            <Chip
              label={`未読 ${data.unreadCount.toLocaleString()} 件`}
              size="small"
              color="primary"
              sx={{ height: 22 }}
            />
          )}
        </Stack>
        <Button
          component={RouterLink}
          to={REGISTRY_CHANGE_ALERT_PATH}
          size="small"
          endIcon={<ChevronRightIcon />}
          sx={{
            fontWeight: 600,
            minHeight: { xs: MIN_TAP_TARGET, [MOBILE_BREAKPOINT]: "auto" },
          }}
        >
          一覧を見てまとめてリユース取得
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        過去に取得した登記より新しい登記に変動があります。
      </Typography>
      <Stack divider={<Divider flexItem />}>
        {data.items.map((alert) => (
          <Stack
            key={alert.id}
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            gap={1}
            sx={{ py: 1 }}
          >
            <Typography sx={{ fontSize: 14, flexGrow: 1, minWidth: 0 }}>
              {alert.location} {alert.chibanKaokuNumber}
              <Typography
                component="span"
                sx={{ ml: 1, fontSize: 12, color: "text.secondary" }}
              >
                {alert.pictureTypeLabel}・{alert.realEstateTypeLabel}
                ／新しい登記 {formatReceivedDate(alert.newerReceivedDate)}
              </Typography>
            </Typography>
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {alert.changedSections.map((section) => (
                <SectionChip key={section.value} label={section.label} />
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
